import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import {
  REGISTER_COUNTRIES,
  applyCountryDialToPhone,
  dialForCountry,
} from '../../client/registerCountries'
import { ibApi, IbApiError } from '../api'

export function IbApplyPage() {
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState('')
  const [notes, setNotes] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await ibApi.apply({
        displayName: displayName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        country: country.trim(),
        notes: notes.trim(),
        password,
      })
      setDone(true)
    } catch (err) {
      setError(err instanceof IbApiError ? err.message : 'Unable to submit application')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="cp-auth cp-auth--register">
      <div className="cp-auth__card cp-auth__card--register">
        <p className="cp-eyebrow cp-auth__eyebrow">IB / Sub-admin</p>
        <h1 className="cp-auth__title">Apply as Partner</h1>
        <p className="cp-muted cp-auth__description">
          Submit your application. Main Admin must approve before you can log in and share your
          referral link.
        </p>

        {done ? (
          <>
            <p className="cp-ok">Application submitted. You will get access after Admin approval.</p>
            <p className="cp-auth__signin">
              Already approved? <Link to="/ib/login">Sign in</Link>
            </p>
          </>
        ) : (
          <>
            <form className="cp-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate={false}>
              <label className="cp-field">
                <span className="cp-field__label">Full name</span>
                <input
                  id="ib-apply-name"
                  name="display_name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={80}
                  autoComplete="name"
                />
              </label>

              <label className="cp-field">
                <span className="cp-field__label">Email</span>
                <input
                  id="ib-apply-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </label>

              <label className="cp-field">
                <span className="cp-field__label">Password (for IB login after approval)</span>
                <input
                  id="ib-apply-password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  maxLength={128}
                  autoComplete="new-password"
                />
              </label>

              <div className="cp-auth__row">
                <label className="cp-field">
                  <span className="cp-field__label">
                    Country <span className="cp-field__optional">(optional)</span>
                  </span>
                  <select
                    id="ib-apply-country"
                    name="country"
                    autoComplete="country-name"
                    value={country}
                    onChange={(e) => {
                      const next = e.target.value
                      setCountry(next)
                      const dial = dialForCountry(next)
                      if (dial) setPhone((prev) => applyCountryDialToPhone(prev, dial))
                    }}
                  >
                    <option value="">Select country</option>
                    {REGISTER_COUNTRIES.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name} ({c.dial})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-field">
                  <span className="cp-field__label">
                    Phone <span className="cp-field__optional">(optional)</span>
                  </span>
                  <input
                    id="ib-apply-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+971 …"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </label>
              </div>

              <label className="cp-field">
                <span className="cp-field__label">
                  Notes <span className="cp-field__optional">(optional)</span>
                </span>
                <textarea
                  id="ib-apply-notes"
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Experience, traffic sources, etc."
                />
              </label>

              {error ? (
                <p className="cp-error cp-auth__alert" role="alert">
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="cp-auth__submit" disabled={busy}>
                {busy ? 'Submitting…' : 'Submit application'}
              </Button>
            </form>
            <p className="cp-auth__signin">
              Already approved? <Link to="/ib/login">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
